import React from "react";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { store } from "@/store";
import { router } from "@/router";

// Global error boundary for unhandled errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

componentDidCatch(error, errorInfo) {
    // Enhanced error logging with more context
    console.error('GLOBAL_ERROR_BOUNDARY: Application error caught:', {
      error: error.toString(),
      message: error.message,
      name: error.name,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });
    
    // Check for specific error types
    const isAuthError = error.message && error.message.includes('AUTH_SAVE_ERROR');
    const isNetworkError = error.message && error.message.includes('Network');
    
    let errorMessage = error.message || error.toString() || 'An unexpected error occurred';
    let toastMessage = `Application Error: ${errorMessage}`;
    
    if (isAuthError) {
      toastMessage = 'Authentication error occurred. Please try logging in again.';
      console.error('AUTH_ERROR_BOUNDARY: Authentication-related error detected');
    } else if (isNetworkError) {
      toastMessage = 'Network error occurred. Please check your connection and try again.';
    } else {
      toastMessage += '. Check console for details. Please refresh the page.';
    }
    
    // Show user-friendly error message
    toast.error(toastMessage, {
      position: "top-right",
      autoClose: isAuthError || isNetworkError ? 8000 : false,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-gray-900 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-6">
              An unexpected error occurred. Please refresh the page to continue.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <RouterProvider router={router} />
        <ToastContainer 
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </Provider>
    </ErrorBoundary>
  );
}

export default App;